import { z } from 'zod';
import { BRLCentsSchema } from './primitives';

/**
 * Evento + Vaga + Dashboard shared schemas.
 *
 * Single source of truth for backend DTOs (createZodDto) and frontend api-client.
 * Framework-agnostic — uses string literal enums (NOT @prisma/client enums) so this
 * package never pulls Prisma client into shared bundles.
 *
 * Status enum mirrors Prisma `EventoStatus` (rascunho/publicado/em_andamento/finalizado/cancelado).
 * Funcao enum mirrors Prisma `Funcao` (MONTADOR/TECNICO/CARREGADOR).
 * TipoContratacao mirrors Prisma `TipoContratacao` (ABERTO/SELECAO).
 */

// ---------- Local (event venue) ----------

// Phase 3 uses free text + UF dropdown (27 BR states).
// Cidade typeahead (IBGE) is deferred to Phase 4 (DISC-02 per CONTEXT.md).
//
// Address validation (CEP × cidade/UF/logradouro):
//   - `cep` is optional in the schema so already-persisted eventos stay valid;
//     the backend requires it on every new write of `local` (EventosService).
//   - `enderecoStatus` is SERVER-OWNED. The only value honored from a client is
//     `divergente_confirmado` (user confirmed a mismatch); the others are
//     recomputed server-side.
export const EnderecoStatusSchema = z.enum([
  'validado', // CEP matches cidade/UF/logradouro
  'divergente_confirmado', // empresa confirmed despite a mismatch
  'nao_verificado', // CEP providers unavailable
]);

export const LocalSchema = z.object({
  endereco: z.string().min(3).max(200),
  cidade: z.string().min(2).max(80),
  uf: z.string().regex(/^[A-Z]{2}$/, 'UF inválida'),
  cep: z.string().regex(/^\d{8}$/, 'CEP inválido').optional(),
  enderecoStatus: EnderecoStatusSchema.optional(),
  lat: z.number().optional(),
  lng: z.number().optional(),
});

// ---------- Vaga ----------

// valorCents is integer cents BRL per Pitfall #2 + Phase 1 schema decision.
// max R$ 100.000,00 = 10_000_000 cents — cap to bound payload size.
export const VagaInputSchema = z.object({
  funcao: z.enum(['MONTADOR', 'TECNICO', 'CARREGADOR']),
  quantidade: z.number().int().positive().max(500),
  valorCents: BRLCentsSchema.max(10_000_000),
  descricao: z.string().max(200).optional(),
});

// VagaDto: server response — includes id + aprovados count.
// TODO Phase 4: aprovados populated from COUNT(Candidatura WHERE status='aprovada').
// Phase 3 always returns 0 since no Candidaturas exist yet.
export const VagaDtoSchema = z.object({
  id: z.string(),
  funcao: z.enum(['MONTADOR', 'TECNICO', 'CARREGADOR']),
  quantidade: z.number().int(),
  valorCents: z.number().int(),
  descricao: z.string().nullable(),
  aprovados: z.number().int().nonnegative(),
});

// ---------- Evento ----------

// CreateEventoSchema — applied via ZodValidationPipe at POST /eventos boundary.
// Refines: dataInicio < dataFim AND dataInicio >= now-60s (allows 1-min clock skew).
export const CreateEventoSchema = z
  .object({
    nome: z.string().min(3).max(120),
    descricao: z.string().max(500),
    dataInicio: z.iso.datetime(),
    dataFim: z.iso.datetime(),
    local: LocalSchema,
    tipoContratacao: z.enum(['ABERTO', 'SELECAO']),
    vagas: z
      .array(VagaInputSchema)
      .min(1, 'Pelo menos 1 vaga')
      .max(10, 'Máximo 10 funções por evento'),
  })
  .refine((d) => new Date(d.dataInicio) < new Date(d.dataFim), {
    message: 'A hora de início deve ser anterior à hora de término',
    path: ['dataFim'],
  })
  .refine((d) => new Date(d.dataInicio) > new Date(Date.now() - 60_000), {
    message: 'dataInicio deve ser futura',
    path: ['dataInicio'],
  })
  .refine(
    (d) => {
      const diffMs = new Date(d.dataFim).getTime() - new Date(d.dataInicio).getTime();
      return diffMs <= 24 * 60 * 60 * 1000;
    },
    {
      message: 'A duração do evento não pode exceder 24 horas (eventos de múltiplos dias não são permitidos)',
      path: ['dataFim'],
    },
  );

// UpdateEventoSchema — partial of the base shape (without refines, since updates
// may omit data*).
// When status !== 'rascunho', service further restricts editable fields to ONLY descricao.
const CreateEventoShape = z.object({
  nome: z.string().min(3).max(120),
  descricao: z.string().max(500),
  dataInicio: z.iso.datetime(),
  dataFim: z.iso.datetime(),
  local: LocalSchema,
  tipoContratacao: z.enum(['ABERTO', 'SELECAO']),
});

export const UpdateEventoSchema = CreateEventoShape.partial();

// ---------- EditPublicado (Phase 9 v1.1 — EDIT-01) ----------
//
// Strict 5-field allowlist for editing a publicado (or pausado) evento. No
// `tipoContratacao` — that field is locked after publish (per CONTEXT.md).
// `horaInicio`/`horaFim` are UI-only — the wizard combines them into
// `dataInicio`/`dataFim` ISO datetimes before POST (mirrors evento-wizard.service.ts).
// Vagas/funcao/valor/quantidade are NOT in this shape — defense-in-depth: backend
// rejects any attempt to mutate them via this route.
const EditPublicadoShape = z.object({
  nome: z.string().min(3).max(120),
  descricao: z.string().max(500),
  dataInicio: z.iso.datetime(),
  dataFim: z.iso.datetime(),
  local: LocalSchema,
});

export const EditPublicadoSchema = EditPublicadoShape.partial()
  .refine(
    (d) => !d.dataInicio || !d.dataFim || new Date(d.dataInicio) < new Date(d.dataFim),
    {
      message: 'A hora de início deve ser anterior à hora de término',
      path: ['dataFim'],
    },
  )
  .refine(
    (d) => !d.dataInicio || new Date(d.dataInicio) > new Date(Date.now() - 60_000),
    {
      message: 'dataInicio deve ser futura',
      path: ['dataInicio'],
    },
  )
  .refine(
    (d) => {
      if (!d.dataInicio || !d.dataFim) return true;
      const diffMs = new Date(d.dataFim).getTime() - new Date(d.dataInicio).getTime();
      return diffMs <= 24 * 60 * 60 * 1000;
    },
    {
      message: 'A duração do evento não pode exceder 24 horas (eventos de múltiplos dias não são permitidos)',
      path: ['dataFim'],
    },
  );

// Status enum mirror (Zod). Value enum mirror lives in `../enums` as EventoStatus.
export const EventoStatusSchema = z.enum([
  'rascunho',
  'publicado',
  'em_andamento',
  'finalizado',
  'cancelado',
  'pausado',
]);

// Full evento DTO (detail view).
export const EventoDtoSchema = z.object({
  id: z.string(),
  empresaId: z.string(),
  nome: z.string(),
  descricao: z.string().nullable(),
  dataInicio: z.string(),
  dataFim: z.string(),
  local: LocalSchema,
  tipoContratacao: z.enum(['ABERTO', 'SELECAO']),
  status: EventoStatusSchema,
  vagas: z.array(VagaDtoSchema),
  totalVagas: z.number().int(),
  totalAprovados: z.number().int(),
  orcamentoCents: z.number().int(),
  criadoEm: z.string(),
  atualizadoEm: z.string(),
});

// Light shape for list views (no vagas[] payload).
export const EventoSummarySchema = z.object({
  id: z.string(),
  nome: z.string(),
  dataInicio: z.string(),
  dataFim: z.string(),
  local: LocalSchema,
  tipoContratacao: z.enum(['ABERTO', 'SELECAO']),
  status: EventoStatusSchema,
  totalVagas: z.number().int(),
  totalAprovados: z.number().int(),
  orcamentoCents: z.number().int(),
});

export const EventoListResponseSchema = z.object({
  eventos: z.array(EventoSummarySchema),
  total: z.number().int(),
});

// ---------- Dashboard ----------

// saldoCents always 0 in Phase 3 — wired in Phase 5 (Carteira). Locked per CONTEXT.
export const DashboardCountsSchema = z.object({
  ativos: z.number().int().nonnegative(),
  finalizados: z.number().int().nonnegative(),
  vagasPublicadas: z.number().int().nonnegative(),
  saldoCents: z.number().int().nonnegative(),
});

// ---------- Filters ----------

// Filter on GET /eventos/me?status= ; "encerrados" maps to (finalizado, cancelado)
// "publicado" maps to (publicado, em_andamento) — em_andamento not surfaced
// as a separate tab in Phase 3 per CONTEXT.
export const EventoStatusFilterSchema = z
  .enum(['rascunho', 'publicado', 'encerrados'])
  .optional();

// ---------- EventoAudit (Phase 9 v1.1 — EDIT-03) ----------

// One audit row per changed field. `oldValue`/`newValue` are unknown JSON values
// (string, ISO datetime, LocalSchema object, EventoStatus string).
// `actorNome` + `actorPapel` are joined server-side; null when the user has been
// deleted (LGPD right-to-be-forgotten — Pitfall #5).
export const EventoAuditDtoSchema = z.object({
  id: z.string(),
  eventoId: z.string(),
  actorId: z.string().nullable(),
  actorNome: z.string().nullable(),
  actorPapel: z.enum(['ADMINISTRADOR', 'COLABORADOR', 'VISUALIZADOR']).nullable(),
  field: z.string(),
  oldValue: z.unknown(),
  newValue: z.unknown(),
  at: z.string(),
});

export const AuditListResponseSchema = z.object({
  rows: z.array(EventoAuditDtoSchema),
  nextCursor: z.string().nullable(),
});

// ---------- Inferred types ----------

export type Local = z.infer<typeof LocalSchema>;
export type EnderecoStatus = z.infer<typeof EnderecoStatusSchema>;
export type VagaInput = z.infer<typeof VagaInputSchema>;
export type VagaDto = z.infer<typeof VagaDtoSchema>;
export type CreateEventoInput = z.infer<typeof CreateEventoSchema>;
export type UpdateEventoInput = z.infer<typeof UpdateEventoSchema>;
export type EditPublicadoInput = z.infer<typeof EditPublicadoSchema>;
export type EventoDto = z.infer<typeof EventoDtoSchema>;
export type EventoSummary = z.infer<typeof EventoSummarySchema>;
export type EventoListResponse = z.infer<typeof EventoListResponseSchema>;
export type DashboardCounts = z.infer<typeof DashboardCountsSchema>;
export type EventoStatusFilter = z.infer<typeof EventoStatusFilterSchema>;
export type EventoAuditDto = z.infer<typeof EventoAuditDtoSchema>;
export type AuditListResponse = z.infer<typeof AuditListResponseSchema>;
// Note: EventoStatus type alias lives in ../enums to avoid duplicate re-export at
// the package root. Use that alias for typing (string-literal union identical to
// the Zod-inferred shape).

// ---------- Ajuste de valor de vaga pós-pagamento ----------
//
// PATCH /eventos/:id/vagas/:vagaId/valor. Aumento → complemento debitado do
// saldo da carteira (+ taxa da plataforma). Redução → só sem candidato
// aprovado; diferença devolvida à carteira. Com candidato aprovado o valor
// nunca pode diminuir.
export const AjustarValorVagaSchema = z.object({
  valorCents: z.number().int().positive().max(10_000_000),
});
export type AjustarValorVagaInput = z.infer<typeof AjustarValorVagaSchema>;

export const AjustarValorVagaResponseSchema = z.object({
  vagaId: z.string(),
  valorCents: z.number().int(),
  tipo: z.enum(['aumento', 'reducao', 'sem_alteracao']),
  // BigInt serializado como string — total movimentado na carteira (diferença + taxa).
  movimentadoCents: z.string(),
});
export type AjustarValorVagaResponse = z.infer<
  typeof AjustarValorVagaResponseSchema
>;
